import { useState } from 'react';
import { ArrowRight, ArrowLeft, RotateCcw, CheckCircle, Home, Hammer } from 'lucide-react';
import { Link } from 'react-router-dom';

export const DecisionHelper = ({ 
  title,
  introText,
  questions,
  outcomes,
  relatedLinks = []
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const handleStart = () => {
    setGameStarted(true);
  };

  const handleAnswer = (optionIndex) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setAnswers(prev => prev.slice(0, -1));
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setGameStarted(false);
    setShowResult(false);
  };

  const calculateOutcome = () => {
    // Calculate scores for each outcome based on answer weights
    const scores = outcomes.map(() => 0);
    
    answers.forEach((answerIndex, questionIndex) => {
      const weights = questions[questionIndex].options[answerIndex].weights;
      weights.forEach((weight, outcomeIndex) => {
        scores[outcomeIndex] += weight;
      });
    });

    // Find the outcome with the highest score
    const maxScore = Math.max(...scores);
    const winningIndex = scores.indexOf(maxScore);
    
    return {
      outcome: outcomes[winningIndex],
      scores,
      confidence: Math.round((maxScore / (answers.length * 2)) * 100)
    };
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Intro screen
  if (!gameStarted) {
    return (
      <div className="quiz-container" data-testid="decision-intro">
        <div className="text-center py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
            {title}
          </h1>
          {introText && (
            <p className="text-lg text-[#475569] mb-8 max-w-xl mx-auto">
              {introText}
            </p>
          )}
          
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mb-8">
            {outcomes.map((outcome, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-[#E2E8F0] p-4"
              >
                <div className="w-12 h-12 rounded-full bg-[#0284C7]/10 flex items-center justify-center mx-auto mb-3">
                  {outcome.icon === 'home' ? (
                    <Home className="text-[#0284C7]" size={24} />
                  ) : (
                    <Hammer className="text-[#0284C7]" size={24} />
                  )}
                </div>
                <div className="font-semibold text-[#0F172A]">{outcome.title}</div>
              </div>
            ))}
          </div>

          <button
            onClick={handleStart}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0284C7] text-white font-semibold rounded-lg hover:bg-[#0369A1] transition-colors duration-200"
            data-testid="decision-start-button"
          >
            Entscheidungshilfe starten
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Result screen
  if (showResult) {
    const result = calculateOutcome();
    const ResultIcon = result.outcome.icon === 'home' ? Home : Hammer;

    return (
      <div className="quiz-container" data-testid="decision-result">
        <div className="result-container animate-scaleIn">
          <div className="w-20 h-20 rounded-full bg-[#0284C7]/10 flex items-center justify-center mx-auto mb-6 score-reveal">
            <ResultIcon className="text-[#0284C7]" size={40} />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2 font-[var(--font-heading)]">
            {result.outcome.title}
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <CheckCircle className="text-green-500" size={18} />
            <span className="text-[#475569]">
              {result.confidence}% Übereinstimmung mit deinen Antworten
            </span>
          </div>

          <p className="result-message">{result.outcome.description}</p>

          {result.outcome.pros && (
            <div className="text-left bg-green-50 rounded-lg p-4 mb-4 max-w-lg mx-auto">
              <h4 className="font-semibold text-green-800 mb-2">Vorteile für dich:</h4>
              <ul className="space-y-1">
                {result.outcome.pros.map((pro, index) => (
                  <li key={index} className="text-green-700 text-sm flex items-start gap-2">
                    <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.outcome.cta && (
            <div className="cta-box max-w-lg mx-auto mb-6">
              <div className="cta-box-title">{result.outcome.cta.title}</div>
              <p className="cta-box-text">{result.outcome.cta.text}</p>
              {result.outcome.cta.link && (
                <a 
                  href={result.outcome.cta.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#0284C7] font-medium hover:underline"
                >
                  {result.outcome.cta.link.label}
                  <ArrowRight size={16} />
                </a>
              )}
            </div>
          )}

          <button
            onClick={handleRestart}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0284C7] text-[#0284C7] font-semibold rounded-lg hover:bg-[#0284C7]/5 transition-colors duration-200"
            data-testid="decision-restart-button"
          >
            <RotateCcw size={18} />
            Nochmal starten
          </button>

          {relatedLinks.length > 0 && (
            <div className="internal-links mt-8">
              <div className="internal-links-title">Weiterlesen:</div>
              <div className="internal-links-list">
                {relatedLinks.map((link, index) => (
                  <Link 
                    key={index} 
                    to={link.path} 
                    className="internal-link"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Question screen
  const question = questions[currentQuestion];

  return (
    <div className="quiz-container" data-testid="decision-question">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-[#475569] mb-2">
          <span>Frage {currentQuestion + 1} von {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="quiz-progress">
          <div 
            className="quiz-progress-bar progress-bar" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 md:p-8">
        <h2 className="quiz-question" data-testid="decision-question-text">
          {question.question}
        </h2>

        <div className="quiz-options">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className="quiz-option quiz-option-btn"
              data-testid={`decision-option-${index}`}
            >
              <span className="quiz-option-letter">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="flex-1 text-left">{option.text}</span>
            </button>
          ))}
        </div>

        {currentQuestion > 0 && (
          <button
            onClick={handleBack}
            className="mt-6 inline-flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] transition-colors"
            data-testid="decision-back-button"
          >
            <ArrowLeft size={18} />
            Zurück
          </button>
        )}
      </div>
    </div>
  );
};

export default DecisionHelper;
